/**
 * Sentry event scrubber.
 *
 * Removes review-token JWTs and any `token` field from a Sentry event before
 * it leaves the browser. Backend-side scrubbing exists too; both sides scrub
 * because either can capture an axios error whose response body contains the
 * leaked token.
 *
 * Walks event.message, event.request, event.exception, event.breadcrumbs,
 * event.extra, and event.contexts. Mutates in place: Sentry's beforeSend
 * contract allows this.
 *
 * Safety:
 *   - Hard depth cap to avoid stack overflow on pathological event shapes.
 *   - WeakSet cycle guard.
 *   - Fail-closed try/catch: if the scrubber itself throws, drop the event
 *     by returning null. A partially scrubbed event could re-leak the token
 *     we're trying to redact.
 */

const JWT_PATTERN = /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+/g;
const MAX_DEPTH = 8;
const SENSITIVE_KEYS = new Set(['token']);

interface SentryEventLike {
  message?: unknown;
  request?: unknown;
  exception?: unknown;
  breadcrumbs?: unknown;
  extra?: unknown;
  contexts?: unknown;
}

const redactString = (value: string): string =>
  value.replace(JWT_PATTERN, '[REDACTED_JWT]');

const scrubInPlace = (
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown => {
  if (depth > MAX_DEPTH) return value;
  if (typeof value === 'string') return redactString(value);
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubInPlace(value[i], depth + 1, seen);
    }
    return value;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      obj[key] = '[REDACTED]';
    } else {
      obj[key] = scrubInPlace(obj[key], depth + 1, seen);
    }
  }
  return value;
};

export const scrubSentryEvent = <T extends SentryEventLike>(
  event: T,
): T | null => {
  try {
    const seen = new WeakSet<object>();
    if (typeof event.message === 'string') {
      event.message = redactString(event.message);
    }
    if (event.request !== undefined) scrubInPlace(event.request, 1, seen);
    if (event.exception !== undefined) scrubInPlace(event.exception, 1, seen);
    if (event.breadcrumbs !== undefined) scrubInPlace(event.breadcrumbs, 1, seen);
    if (event.extra !== undefined) scrubInPlace(event.extra, 1, seen);
    if (event.contexts !== undefined) scrubInPlace(event.contexts, 1, seen);
    return event;
  } catch {
    return null;
  }
};
